import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getUserProfile } from '@store/common/selectors';
import { useWindowSize } from 'react-use';
import { Table, Button, type TableProps, message } from 'antd';
import { useSetAtom } from 'jotai';
import * as atoms from '@src/components/user-management/store/atom';
import { getColumns, reconcileSelectedItems } from './utils';
import SearchBox from './SearchBox';
import * as Styled from './styled';
import { LoginUserProfile, UserProfileInfo } from '../../types';
import { Retry } from '@src/components/common';
import { useGetUserList, useGetUserMeta, UserListFilters } from '../../hooks';
import { MAX_USER_COUNT_TO_OPERATE } from '../utils';

// Permission columns that participate in the single-permission server filter (dataIndex === permKey).
// Role is filtered separately (server allows it alongside a permission filter).
const PERMISSION_FILTER_KEYS = new Set([
  'ciEnabled',
  'mdsEnabled',
  'canEditPrecisionPolicy',
  'canCreateActivation',
  'nexaPermission',
  'apiManagementPermission',
]);

const UserList = () => {
  const user = useSelector<any, LoginUserProfile>(getUserProfile);
  const { data: { permissionDefinitions = [], roleList = [] } = {} } = useGetUserMeta();
  const setInviteUser = useSetAtom(atoms.toInviteUserAtom);
  const windowSize = useWindowSize();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchText, setSearchText] = useState('');
  // Server-side column filters (map ticket T8/T9): one permission filter + optional role.
  const [permFilter, setPermFilter] = useState<{ key: string; value: string } | undefined>();
  const [roleFilter, setRoleFilter] = useState<number | undefined>();
  const setUsersToRevoke = useSetAtom(atoms.usersToRevokeAtom);
  const setUsersToBulkEdit = useSetAtom(atoms.usersToBulkEditAtom);
  const [selectedItems, setSelectedItems] = useState<UserProfileInfo[]>([]);
  const idsToDelete = React.useMemo(() => {
    // Defensive: never let a stray undefined entry (e.g. an unresolved preserved selection) throw
    // here — a crash in this render unmounts the whole User Management panel and it can't be
    // reopened without a full page refresh.
    return selectedItems.filter(Boolean).map((it) => it.userId);
  }, [selectedItems]);

  const filters = useMemo<UserListFilters>(
    () => ({
      search: searchText || undefined,
      roleId: roleFilter,
      permKey: permFilter?.key,
      permValue: permFilter?.value,
    }),
    [searchText, roleFilter, permFilter],
  );

  const {
    data: userList = [],
    isFetching: isRequestingUserList,
    isError: isRequestUserListError,
    refetch: refetchUserList,
  } = useGetUserList({ filters });

  // The full set of permission-filter column keys (static legacy + dynamic KV booleans), used to
  // tell a permission-column filter change apart from the role filter in the Table onChange.
  const permissionFilterKeys = useMemo(() => {
    const keys = new Set(PERMISSION_FILTER_KEYS);
    for (const def of permissionDefinitions) {
      if (def.kind === 'boolean' && def.storage?.kind === 'kv') {
        keys.add(def.key);
      }
    }
    return keys;
  }, [permissionDefinitions]);

  const columns = useMemo(
    () =>
      getColumns({
        user,
        permissionDefinitions,
        roleList,
        activePermKey: permFilter?.key,
        activePermValue: permFilter?.value,
        activeRoleId: roleFilter,
      }),
    [user, permissionDefinitions, roleList, permFilter, roleFilter],
  );

  // Translate the Table's filter change into our lifted server-filter state. Enforces the single
  // permission filter: whichever permission column changed becomes the active one (others are
  // already disabled in the UI). Role filters independently.
  const handleTableChange = useCallback<NonNullable<TableProps<UserProfileInfo>['onChange']>>(
    (_pagination, tableFilters) => {
      // role
      const roleVals = tableFilters.roleName as (string | number)[] | null;
      setRoleFilter(roleVals && roleVals.length ? Number(roleVals[0]) : undefined);
      // permission: find the permission column that has a value
      let nextPerm: { key: string; value: string } | undefined;
      for (const [dataIndex, vals] of Object.entries(tableFilters)) {
        if (permissionFilterKeys.has(dataIndex) && vals && (vals as unknown[]).length) {
          nextPerm = { key: dataIndex, value: String((vals as unknown[])[0]) };
          break;
        }
      }
      setPermFilter(nextPerm);
    },
    [permissionFilterKeys],
  );

  const rowSelection = useMemo<TableProps<UserProfileInfo>['rowSelection']>(
    () => ({
      type: 'checkbox',
      columnWidth: 36,
      preserveSelectedRowKeys: true,
      selectedRowKeys: idsToDelete,
      selections: [
        {
          key: 'clear',
          text: 'Clear all selections',
          onSelect: () => setSelectedItems([]),
        },
      ],
      onChange: (selectedKeys, selectedRecords) => {
        // With `preserveSelectedRowKeys`, a key can stay selected across a server-side search/filter
        // change even when its row is no longer in the current dataSource — antd then hands us an
        // `undefined` record for it, which crashes anything mapping `it.userId` (unmounting the whole
        // panel). Reconcile by key against previously-selected items so nothing is undefined.
        setSelectedItems((prev) => reconcileSelectedItems(selectedKeys, selectedRecords, prev));
      },
    }),
    [idsToDelete],
  );

  // Reset to page 1 when the FILTERS change (not on every data refresh) — a background refetch with
  // the same filters shouldn't bounce the user off their current page. `filters` is a memoized
  // object whose reference only changes when search/role/permission change.
  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  const handleOnInviteUser = () => {
    setInviteUser(true);
  };

  const handleRevoke = () => {
    if (selectedItems.length > MAX_USER_COUNT_TO_OPERATE) {
      message.warning('User limit exceeded. You can add or revoke up to 50 users at a time.');
      return;
    }
    setUsersToRevoke(selectedItems);
  };

  const handleBulkEdit = () => {
    if (selectedItems.length === 0) {
      message.warning('Select one or more users to edit permissions.');
      return;
    }
    if (selectedItems.length > MAX_USER_COUNT_TO_OPERATE) {
      message.warning(
        `User limit exceeded. You can edit permissions for up to ${MAX_USER_COUNT_TO_OPERATE} users at a time.`,
      );
      return;
    }
    setUsersToBulkEdit(selectedItems);
  };

  const renderContent = () => {
    return (
      <Retry
        isLoading={isRequestingUserList}
        showError={isRequestUserListError}
        fetchData={refetchUserList}
      >
        <Table
          rowKey="userId"
          sticky
          size="small"
          scroll={{ x: 900, y: 'calc(100% - 36px)', scrollToFirstRowOnChange: true }}
          rowSelection={rowSelection}
          dataSource={userList}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: pageNumber,
            onChange: setPageNumber,
            pageSize: windowSize.height > 1000 ? 50 : 25,
            showSizeChanger: false,
          }}
        />
      </Retry>
    );
  };

  return (
    <>
      <Styled.TopSection>
        <Styled.TopBar>
          <SearchBox
            defaultValue={searchText}
            hiddenEnabled={user.hidden}
            onSearch={setSearchText}
          />
          <Button type="primary" ghost disabled={!selectedItems.length} onClick={handleBulkEdit}>
            Edit Permissions{!!selectedItems.length && ` (${selectedItems.length})`}
          </Button>
          <Button type="primary" danger ghost onClick={handleRevoke}>
            Revoke User{!!selectedItems.length && `(${selectedItems.length})`}
          </Button>
          {
            <Button type="primary" ghost onClick={handleOnInviteUser}>
              + Invite User
            </Button>
          }
        </Styled.TopBar>
      </Styled.TopSection>
      <Styled.TableContainer>{renderContent()}</Styled.TableContainer>
    </>
  );
};

export default React.memo(UserList);
